export interface CookieConsent {
  essential: boolean; // Always true, cannot be disabled
  analytics: boolean;
  personalization: boolean;
  marketing: boolean;
  timestamp: string;
  version: string;
}

const COOKIE_CONSENT_KEY = 'reklaim_cookie_consent';
const CONSENT_VERSION = '1.0';

export const getDefaultConsent = (): CookieConsent => ({
  essential: true,
  analytics: false,
  personalization: false,
  marketing: false,
  timestamp: new Date().toISOString(),
  version: CONSENT_VERSION,
});

export const getStoredConsent = (): CookieConsent | null => {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) return null;
    
    const consent = JSON.parse(stored) as CookieConsent;
    
    // Check if consent version matches current version
    if (consent.version !== CONSENT_VERSION) {
      // Version mismatch - require re-consent
      return null;
    }
    
    return consent;
  } catch {
    return null;
  }
};

export const saveConsent = (consent: CookieConsent): void => {
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      ...consent,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    }));
  } catch (error) {
    console.error('Failed to save cookie consent:', error);
  }
};

export const hasConsented = (): boolean => {
  return getStoredConsent() !== null;
};

export const hasAnalyticsConsent = (): boolean => {
  const consent = getStoredConsent();
  return consent?.analytics ?? false;
};

export const hasPersonalizationConsent = (): boolean => {
  const consent = getStoredConsent();
  return consent?.personalization ?? false;
};

export const hasMarketingConsent = (): boolean => {
  const consent = getStoredConsent();
  return consent?.marketing ?? false;
};

export const acceptAllCookies = (): CookieConsent => {
  const consent: CookieConsent = {
    essential: true,
    analytics: true,
    personalization: true,
    marketing: true,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  saveConsent(consent);
  return consent;
};

export const rejectNonEssentialCookies = (): CookieConsent => {
  const consent: CookieConsent = {
    essential: true,
    analytics: false,
    personalization: false,
    marketing: false,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  saveConsent(consent);
  return consent;
};

export const updateConsent = (updates: Partial<Omit<CookieConsent, 'essential' | 'timestamp' | 'version'>>): CookieConsent => {
  const current = getStoredConsent() || getDefaultConsent();
  const consent: CookieConsent = {
    ...current,
    ...updates,
    essential: true, // Essential cookies cannot be disabled
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  saveConsent(consent);
  return consent;
};

export const clearConsent = (): void => {
  try {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
  } catch (error) {
    console.error('Failed to clear cookie consent:', error);
  }
};
